import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Loader2, CheckCircle2, XCircle, CalendarDays, MapPin, Users, PartyPopper,
  ShieldCheck,
} from "lucide-react";
import InsuranceWarningBanner from "@/components/InsuranceWarningBanner";

/**
 * PublicBooking — the customer's page for a booking they requested.
 *
 * URL shape: `/book/{id}?t={signed_token}[&paid=1|0]`
 *   - `t` is the HMAC token they got in the quote/confirmation email.
 *   - `paid=1` on return from Stripe Checkout success.
 *   - `paid=0` on cancel.
 *
 * Rendering:
 *   - status="requested": "Vendor is reviewing — we'll email when they quote."
 *   - status="quoted": Show the quote + Pay button + insurance warning.
 *   - status="paid" (or paid=1 in URL): confirmation + receipt.
 *   - status="cancelled" / "declined": clear closure copy.
 *
 * Cancel button appears on "quoted" (no charge yet) and "paid" (refund per
 * ladder). The refund preview is surfaced before the customer confirms
 * cancellation so there's no surprise.
 */
export default function PublicBooking() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const paidFlag = params.get("paid");

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/private-bookings/${id}`,
        { params: { token } });
      setBooking(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "This booking link is no longer valid.";
      toast.error(msg);
    } finally { setLoading(false); }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);
  // If we just came back from a Stripe success, poll for a few seconds so
  // the webhook has time to flip the status to "paid".
  useEffect(() => {
    if (paidFlag !== "1" || booking?.status === "paid") return;
    const start = Date.now();
    const t = setInterval(() => {
      if (Date.now() - start > 20000) { clearInterval(t); return; }
      load();
    }, 2500);
    return () => clearInterval(t);
  }, [paidFlag, booking?.status, load]);

  const pay = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/private-bookings/${id}/pay`,
        { token, origin_url: window.location.origin });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Couldn't start checkout");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Couldn't start checkout";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (busy) return;
    const refundHint = booking?.status === "paid"
      ? `Refund per policy: $${(((booking.quote_cents || 0) * (booking.refund_bps_if_cancelled_today || 0) / 10000) / 100).toFixed(2)} of $${((booking.quote_cents || 0) / 100).toFixed(2)}.`
      : "You haven't been charged yet.";
    if (!window.confirm(`Cancel this booking?\n\n${refundHint}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/private-bookings/${id}/cancel`,
        { token });
      toast.success(data.refund_amount_cents > 0
        ? `Cancelled — refund of $${(data.refund_amount_cents/100).toFixed(2)} is processing.`
        : "Cancelled.");
      load();
    } catch (e) {
      toast.error("Couldn't cancel booking");
    } finally { setBusy(false); }
  };

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="public-booking-loading">
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--avt-purple)" }} />
    </div>
  );
  if (!booking) return (
    <div className="mx-auto max-w-lg px-6 py-16 text-center" data-testid="public-booking-invalid">
      <h1 className="font-serif text-3xl">This link isn't valid.</h1>
      <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
        Please check your email for the correct link, or reach out to your vendor directly.
      </p>
    </div>
  );

  const isPaid = booking.status === "paid" || paidFlag === "1";

  return (
    <div className="mx-auto max-w-2xl px-6 py-10" data-testid="public-booking-page">
      <div className="overline" style={{ color: "var(--avt-purple)" }}>
        Your booking with <strong>{booking.vendor_name}</strong>
      </div>
      <h1 className="font-serif text-4xl mt-2">
        {booking.event_type}
      </h1>

      <div className="mt-4 flex flex-wrap gap-4 text-sm">
        <Meta icon={CalendarDays}>{booking.event_date}{booking.event_time ? ` · ${booking.event_time}` : ""}</Meta>
        <Meta icon={MapPin}>{booking.event_location}</Meta>
        {booking.guest_count && <Meta icon={Users}>{booking.guest_count} guests</Meta>}
      </div>

      {/* Status-driven main panel */}
      <div className="mt-8">
        {booking.status === "requested" && <RequestedPanel />}

        {booking.status === "quoted" && !isPaid && (
          <QuotedPanel booking={booking} onPay={pay} onCancel={cancel} busy={busy} />
        )}

        {isPaid && <PaidPanel booking={booking} onCancel={cancel} busy={busy} />}

        {booking.status === "declined" && (
          <ClosurePanel
            icon={XCircle}
            color="#5b524a"
            title={`${booking.vendor_name} isn't able to take this one.`}
            body={booking.decline_reason || "We're sorry it didn't line up. Browse other vendors on AddVendTour who might be a match."}
            ctaLabel="Discover more vendors"
            ctaHref="/vendors"
          />
        )}

        {booking.status === "cancelled" && (
          <ClosurePanel
            icon={XCircle}
            color="#5b524a"
            title="This booking has been cancelled."
            body={
              (booking.refund_amount_cents || 0) > 0
                ? `A refund of $${((booking.refund_amount_cents || 0)/100).toFixed(2)} is on its way.`
                : "No refund per the cancellation policy — see the payment protection notice below for how disputes work."
            }
          />
        )}
      </div>

      {booking.wishlist && (
        <div className="mt-6 rounded-md p-4" style={{ backgroundColor: "var(--sand)" }}>
          <div className="text-[10px] uppercase tracking-widest mb-1"
               style={{ color: "var(--ink-soft)" }}>What you asked for</div>
          <div className="text-sm whitespace-pre-wrap">{booking.wishlist}</div>
        </div>
      )}

      {/* Insurance banner is ALWAYS shown on this page — before, during, and
          after payment — so the guarantee is visible whenever the customer
          revisits the link. */}
      <div className="mt-6">
        <InsuranceWarningBanner />
      </div>
    </div>
  );
}

function Meta({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2" style={{ color: "var(--ink)" }}>
      <Icon className="h-4 w-4" style={{ color: "var(--avt-purple)" }} />
      <span>{children}</span>
    </div>
  );
}

function RequestedPanel() {
  return (
    <div className="card-warm rounded-2xl p-6 text-center" data-testid="booking-status-requested">
      <PartyPopper className="h-8 w-8 mx-auto" style={{ color: "var(--avt-purple)" }} />
      <h2 className="mt-3 font-serif text-2xl">Your request is in.</h2>
      <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
        The vendor will review and reply with a firm quote — we'll email you
        the moment they do. Save this page so you can come back and pay.
      </p>
    </div>
  );
}

function QuotedPanel({ booking, onPay, onCancel, busy }) {
  const price = `$${(((booking.quote_cents) || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  return (
    <div className="card-warm rounded-2xl p-6" data-testid="booking-status-quoted">
      <div className="overline" style={{ color: "var(--avt-purple)" }}>Your quote is in</div>
      <div className="mt-1 font-serif text-4xl" style={{ color: "var(--avt-purple)" }}>{price}</div>
      {booking.quote_message && (
        <div className="mt-4 rounded-md p-4" style={{ backgroundColor: "var(--sand)" }}>
          <div className="text-[10px] uppercase tracking-widest mb-1"
               style={{ color: "var(--ink-soft)" }}>Note from {booking.vendor_name}</div>
          <div className="text-sm whitespace-pre-wrap">{booking.quote_message}</div>
        </div>
      )}
      <div className="mt-6">
        <InsuranceWarningBanner />
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <Button onClick={onPay} disabled={busy}
                data-testid="booking-pay-btn"
                className="btn-primary rounded-full flex-1 min-w-[220px]">
          {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
                : <><ShieldCheck className="h-4 w-4 mr-2" /> Pay {price} securely with Stripe</>}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={busy}
                data-testid="booking-cancel-btn"
                className="rounded-full">
          <XCircle className="h-4 w-4 mr-1.5" /> Cancel booking
        </Button>
      </div>
    </div>
  );
}

function PaidPanel({ booking, onCancel, busy }) {
  const price = `$${(((booking.quote_cents) || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  const refundToday = ((booking.quote_cents || 0) * (booking.refund_bps_if_cancelled_today || 0) / 10000) / 100;
  return (
    <div className="card-warm rounded-2xl p-6" data-testid="booking-status-paid">
      <CheckCircle2 className="h-8 w-8" style={{ color: "#1f5d34" }} />
      <h2 className="mt-3 font-serif text-2xl">You're set with {booking.vendor_name}.</h2>
      <p className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>
        {price} paid. Save this page — it's your receipt and your point of contact
        if anything changes.
      </p>
      <div className="mt-4 text-xs" style={{ color: "var(--ink-soft)" }}>
        Refund policy: 100% if cancelled 14+ days out · 50% if 6–13 days · 0% within 5 days.
        {" "}Cancelled today it would be <strong>${refundToday.toFixed(2)}</strong>.
      </div>
      <div className="mt-5">
        <Button variant="outline" onClick={onCancel} disabled={busy}
                data-testid="booking-cancel-btn"
                className="rounded-full">
          <XCircle className="h-4 w-4 mr-1.5" /> Cancel booking
        </Button>
      </div>
    </div>
  );
}

function ClosurePanel({ icon: Icon, color, title, body, ctaLabel, ctaHref }) {
  return (
    <div className="card-warm rounded-2xl p-6" data-testid="booking-status-closure">
      <Icon className="h-8 w-8" style={{ color }} />
      <h2 className="mt-3 font-serif text-2xl">{title}</h2>
      <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>{body}</p>
      {ctaLabel && ctaHref && (
        <Link to={ctaHref}
              className="mt-4 inline-flex items-center gap-1.5 text-sm underline"
              style={{ color: "var(--avt-purple)" }}>
          {ctaLabel}
        </Link>
      )}
    </div>
  );
}
